import { useState, useEffect, useCallback } from 'react';
import { fetchBlockPreviews, fetchProfileView } from '@ego/api';
import { relativeTime } from '@ego/domain';
import useAuth from './useAuth';
import { groupBlockPreviews, padPreviews } from '../utils/groupBlockPreviews';

export default function useChannels(handle) {
  const { session } = useAuth();
  const [profile, setProfile] = useState(null);
  const [channels, setChannels] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [lastModified, setLastModified] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (showLoading = true) => {
    if (!handle) {
      setProfile(null);
      setChannels([]);
      setIsOwner(false);
      setLastModified(null);
      setLoading(false);
      return;
    }

    if (showLoading) setLoading(true);
    try {
      const view = await fetchProfileView(handle, session?.access_token);
      setProfile(view.profile);
      setIsOwner(!!view.isOwner);

      const mostRecentUpdated = view.channels.reduce((latest, ch) => {
        const t = ch.updatedAt ? new Date(ch.updatedAt) : null;
        return t && (!latest || t > latest) ? t : latest;
      }, null);
      setLastModified(mostRecentUpdated ? relativeTime(mostRecentUpdated.toISOString()) : null);

      const openIds = view.channels
        .filter(ch => view.isOwner || ch.visibility === 'public')
        .map(ch => ch.id);
      const previewBlocks = await fetchBlockPreviews(openIds, { handle, accessToken: session?.access_token });
      const previewsByChannel = groupBlockPreviews(previewBlocks);

      setChannels(view.channels.map(channel => {
        const previews = padPreviews(previewsByChannel[channel.id] || []);

        return {
          id: channel.id,
          title: channel.title,
          slug: channel.slug,
          handle: channel.handle,
          author: view.profile.name,
          accentColor: channel.accentColor,
          borderColor: channel.borderColor,
          blocksCount: channel.blocksCount,
          updatedRelative: relativeTime(channel.updatedAt),
          previews,
          visibility: channel.visibility,
          hasPassword: channel.hasPassword,
        };
      }));
    } catch (err) {
      console.error('[useChannels] Failed to load profile view:', err);
      if (showLoading) {
        setProfile(null);
        setChannels([]);
        setIsOwner(false);
        setLastModified(null);
      }
    } finally {
      setLoading(false);
    }
  }, [handle, session?.access_token]);

  useEffect(() => {
    void load(true);
  }, [load]);

  const refetch = useCallback(() => load(false), [load]);

  return { profile, channels, isOwner, loading, lastModified, refetch };
}
